import { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  findManyPurchaseOrders,
  findOneJob,
  findOneSubdivision,
} from 'services/jobs';
import { createContainer } from 'unstated-next';
import {
  BoardPurchaseDto,
  JobDto,
  JobUpdateDto,
  PhaseDto,
  SubdivisionDto,
} from '@vatos-pas/common';
import { Point } from 'geojson';
import { ParsedPlace, geoPointToLatLng } from 'components/Map';
import { AddressEditorField } from '../components/address-editor';

const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

export type JobAddressEdit = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  latitude: number | null;
  longitude: number | null;
};

const INITIAL_ADDRESS_EDIT: JobAddressEdit = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  latitude: null,
  longitude: null,
};

export type AddressValidationResult = {
  valid: boolean;
  errors: Record<string, string | undefined>;
};

const useJob = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [selectedPurchaseOrder, setSelectedPurchaseOrder] =
    useState<BoardPurchaseDto | null>(null);
  const [isPurchaseOrderOpen, setIsPurchaseOrderOpen] = useState(false);
  const [job, setJob] = useState<JobDto | null>(null);
  const [subdivision, setSubdivision] = useState<SubdivisionDto | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<
    BoardPurchaseDto[] | null
  >(null);
  const [startingPhase, setStartingPhase] = useState<PhaseDto | null>(null);

  const [editAddress, setEditAddress] = useState<JobAddressEdit>(
    INITIAL_ADDRESS_EDIT,
  );
  const [addressErrors, setAddressErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  const handleSelectedPurchaseOrder = (purchaseOrder: BoardPurchaseDto) => {
    setIsPurchaseOrderOpen(true);
    setSelectedPurchaseOrder(purchaseOrder);
  };

  const seedAddressFromJob = (loaded: JobDto) => {
    const { latitude, longitude } = geoPointToLatLng(loaded.geo);
    setEditAddress({
      address1: loaded.address1 ?? '',
      address2: loaded.address2 ?? '',
      city: loaded.city ?? '',
      state: loaded.state ?? '',
      zip: loaded.zip ?? '',
      latitude,
      longitude,
    });
    setAddressErrors({});
  };

  const loadJobData = async (jobId?: string) => {
    if (!jobId) return;

    const job = await findOneJob(dataProvider, jobId);

    if (!job) return;

    const subdivision = await findOneSubdivision(
      dataProvider,
      job.model?.subdivisionId,
    );
    const purchaseOrders = await findManyPurchaseOrders(dataProvider, job.id);

    if (job.startingPhaseId) {
      try {
        const phase = await dataProvider.getOne<PhaseDto>('phase', {
          id: job.startingPhaseId,
        });
        setStartingPhase(phase.data);
      } catch {
        setStartingPhase(null);
      }
    }

    setJob(job);
    setSubdivision(subdivision);
    setPurchaseOrders(purchaseOrders);
    seedAddressFromJob(job);
  };

  const setAddressField = (field: AddressEditorField, value: string) => {
    setEditAddress(prev =>
      prev[field] === value ? prev : { ...prev, [field]: value },
    );
    setAddressErrors(prev =>
      prev[field] === undefined ? prev : { ...prev, [field]: undefined },
    );
  };

  const applyParsedPlace = (parsed: ParsedPlace) => {
    setEditAddress(prev => ({
      ...prev,
      address1: parsed.address1,
      address2: parsed.address2,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
      latitude: Number.isFinite(parsed.lat) ? parsed.lat : null,
      longitude: Number.isFinite(parsed.lng) ? parsed.lng : null,
    }));
  };

  const updateCoordinates = (lat: number, lng: number) => {
    setEditAddress(prev => ({ ...prev, latitude: lat, longitude: lng }));
  };

  const savedCoords = geoPointToLatLng(job?.geo);
  const hasAddressChanges =
    !!job &&
    (editAddress.address1 !== (job.address1 ?? '') ||
      editAddress.address2 !== (job.address2 ?? '') ||
      editAddress.city !== (job.city ?? '') ||
      editAddress.state !== (job.state ?? '') ||
      editAddress.zip !== (job.zip ?? '') ||
      editAddress.latitude !== savedCoords.latitude ||
      editAddress.longitude !== savedCoords.longitude);

  const validateAddress = (): AddressValidationResult => {
    const errors: Record<string, string | undefined> = {};
    if (!editAddress.address1) {
      errors.address1 = 'The Address is Required';
    }
    if (!editAddress.city) {
      errors.city = 'The City is Required';
    }
    if (!editAddress.state) {
      errors.state = 'The State is Required';
    }
    if (!editAddress.zip) {
      errors.zip = 'The Zip Code is Required';
    } else if (!editAddress.zip.match(ZIP_CODE_REGEX)) {
      errors.zip =
        'Zip code must be entered either in 5 digit format (12345) or 9 digit format (12345-1234)';
    }
    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const saveAddress = async () => {
    if (!job?.id) return;

    const validation = validateAddress();
    if (!validation.valid) {
      setAddressErrors(validation.errors);
      return;
    }

    setIsSavingAddress(true);
    try {
      const geo: Point | null =
        editAddress.latitude !== null && editAddress.longitude !== null
          ? {
              type: 'Point',
              coordinates: [editAddress.longitude, editAddress.latitude],
            }
          : null;

      const data: JobUpdateDto = {
        address1: editAddress.address1,
        address2: editAddress.address2,
        city: editAddress.city,
        state: editAddress.state,
        zip: editAddress.zip,
        geo,
      };

      await dataProvider.update('job', {
        id: job.id,
        data,
        previousData: job,
      });
      notify('Job address updated. Log distances have been recalculated.');
      setAddressErrors({});
      await loadJobData(job.id);
    } catch (error) {
      notify('Error on Update Address', 'warning');
    } finally {
      setIsSavingAddress(false);
    }
  };

  return {
    loadJobData,
    job,
    subdivision,
    startingPhase,
    purchaseOrders,
    isPurchaseOrderOpen,
    setIsPurchaseOrderOpen,
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
    handleSelectedPurchaseOrder,
    editAddress,
    hasAddressChanges,
    addressErrors,
    isSavingAddress,
    setAddressField,
    applyParsedPlace,
    updateCoordinates,
    validateAddress,
    saveAddress,
  };
};

export const JobContainer = createContainer(useJob);
